import React, { useCallback } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import {
  IGetUserOrdersQuery,
  IGetUserOrdersQueryVariables,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';

import { useCartHydration } from './use-cart-hydration.ts';

export const UserOrdersDefaultLimit = 5;

export const UserOrdersDefaultVariables = {
  orderStatuses: [
    RbiOrderStatus.INSERT_SUCCESSFUL,
    RbiOrderStatus.UPDATE_SUCCESSFUL,
    RbiOrderStatus.REFUND_SUCCESSFUL,
    RbiOrderStatus.REFUND_ERROR,
    RbiOrderStatus.REFUND_REQUESTED,
  ],
  shouldFilterByRegion: true,
};

export const useUserOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { store } = useStoreContext();
  const { prepareReorderCartEntries } = useCartHydration({
    minimalCartEntries: [],
    cartEntries: [],
  });

  const [ordersData, setOrdersData] = React.useState<IGetUserOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState<boolean>(false);
  const [isHydratingOrder, setIsHydratingOrder] = React.useState<boolean>(false);
  const { isAuthenticated } = useAuthContext();

  const hydrateOrders = useCallback(
    async (orders: IHistoricalOrder[]) => {
      const result = [];

      for (const order of orders) {
        try {
          const hydratedBackendCartEntries = await prepareReorderCartEntries({
            order,
            newStore: store,
          });

          result.push({
            ...order,
            cart: {
              ...order.cart,
              cartEntries: hydratedBackendCartEntries,
            },
          } as IHistoricalOrder);
        } catch {
          // fallback
          result.push(order);
        }
      }
      return result;
    },
    [prepareReorderCartEntries, store]
  );

  const { error, loading, refetch, fetchMore } = useGetUserOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    onCompleted: async data => {
      setIsHydratingOrder(true);

      try {
        const orders = ((data.userOrders.orders || []) as unknown) as IHistoricalOrder[];
        const hydrated = await hydrateOrders(orders);

        const userOrders = {
          ...data,
          userOrders: {
            ...data.userOrders,
            orders: hydrated,
          },
        };

        setOrdersData(userOrders);
      } finally {
        setIsHydratingOrder(false);
      }
    },
    context: { shouldNotBatch: true },
    skip: !isAuthenticated,
    fetchPolicy,
  });

  const resumeAfter = ordersData?.userOrders.resumeAfter;
  const count = ordersData?.userOrders.count || 0;
  const orders = ((ordersData?.userOrders.orders || []) as unknown) as IHistoricalOrder[];

  const handleLoadMore = useCallback(async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/recentitems',
    });

    setFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: { resumeAfter },
      });

      const prevOrders = ordersData?.userOrders.orders || [];
      const currentOrders = fetchMoreResult.userOrders.orders || [];

      const hydrated = await hydrateOrders((currentOrders as unknown) as IHistoricalOrder[]);

      const userOrders = {
        ...fetchMoreResult,
        userOrders: {
          ...fetchMoreResult.userOrders,
          orders: [...prevOrders, ...hydrated],
        },
      };

      setOrdersData(userOrders);
      return userOrders;
    } finally {
      setFetchingMore(false);
    }
  }, [fetchMore, hydrateOrders, ordersData?.userOrders.orders, resumeAfter]);

  return {
    error,
    loading,
    orders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    isHydratingOrder,
  };
};
